import { format } from 'date-fns-tz';
import i18n from "i18next";
import { getIntlLocale } from "./locale";

export function formatDate(date) {
  if (date === null || date === undefined || date === '') return '-';
  const d =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;
  const lang = getIntlLocale(i18n.language);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatLocalizedDateTime(date, options = {}) {
  const includeSeconds = options?.includeSeconds ?? false;
  const lang = getIntlLocale(i18n.language);
  const str = date.toLocaleString(lang, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds ? { second: '2-digit' } : {})
  });

  return str.replace(/,\s?/, ' - ');
}

export function formatDateTime(date, options = {}) {
  if (date === null || date === undefined || date === '') return '-';
  const d =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '-';
  return formatLocalizedDateTime(d, options);
}

export function formatConversionDateTimeLabel(value, options = {}) {
  if (value === null || value === undefined || value === "") return value;
  const includeSeconds = options?.includeSeconds ?? true;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatLocalizedDateTime(value, { includeSeconds });
  }

  if (typeof value === "number") {
    const parsedNumberDate = new Date(value);
    if (!Number.isNaN(parsedNumberDate.getTime())) {
      return formatLocalizedDateTime(parsedNumberDate, { includeSeconds });
    }
    return value;
  }

  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  const normalized = trimmed.replace(/\s*-\s*/, " ");
  const slashPattern = normalized.match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/
  );

  if (slashPattern) {
    const [, dayRaw, monthRaw, yearRaw, hourRaw, minuteRaw, secondRaw] = slashPattern;
    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    const second = Number(secondRaw || 0);
    const parsedSlashDate = new Date(year, month - 1, day, hour, minute, second);

    if (
      !Number.isNaN(parsedSlashDate.getTime()) &&
      parsedSlashDate.getDate() === day &&
      parsedSlashDate.getMonth() === month - 1 &&
      parsedSlashDate.getFullYear() === year
    ) {
      return formatLocalizedDateTime(parsedSlashDate, {
        includeSeconds: Boolean(secondRaw) && includeSeconds
      });
    }
  }

  const parsedDate = new Date(trimmed);
  if (!Number.isNaN(parsedDate.getTime())) {
    const hasSeconds = /:\d{2}:\d{2}/.test(trimmed);
    return formatLocalizedDateTime(parsedDate, {
      includeSeconds: hasSeconds && includeSeconds
    });
  }

  const fallbackMatch = trimmed.match(
    /^(\d{2}\/\d{2}\/\d{4})\s+(\d{2}:\d{2}(?::\d{2})?)$/
  );
  if (fallbackMatch) {
    const fallbackTime = includeSeconds
      ? fallbackMatch[2]
      : fallbackMatch[2].replace(/:\d{2}$/, "");
    return `${fallbackMatch[1]} - ${fallbackTime}`;
  }

  return trimmed;
}

/**
 * Formato usado para enviar para a API, como 'yyyy-MM-dd'
 */
export function formatDateLocal(date) {
  return format(date, 'yyyy-MM-dd', { timeZone: 'UTC' });
}
